import React, { useState, useEffect, useRef, useCallback } from 'react';

const WhatsAppButton = () => {
  const [phase, setPhase] = useState('hidden');
  const [dismissed, setDismissed] = useState(false);
  const startedRef = useRef(false);
  const audioRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  // Unlock audio on first interaction, then start sequence
  const onFirstInteraction = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    // Create and unlock audio immediately during user gesture
    const audio = new Audio('/notification.wav');
    audio.volume = 0;
    audio.play().then(() => {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 1;
      audioRef.current = audio;
      audioUnlockedRef.current = true;
    }).catch(() => {});

    // Start animation sequence
    setTimeout(() => setPhase('typing'), 1000);
    setTimeout(() => {
      setPhase('message');
      // Play sound
      if (audioRef.current && audioUnlockedRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.volume = 1;
        audioRef.current.play().catch(() => {});
      }
    }, 3500);
  }, []);

  useEffect(() => {
    const events = ['scroll', 'click', 'touchstart', 'mousemove', 'keydown'];
    const handler = () => {
      onFirstInteraction();
      events.forEach(e => window.removeEventListener(e, handler));
    };
    events.forEach(e => window.addEventListener(e, handler, { once: true, passive: true }));

    // Fallback without sound after 8s
    const fallback = setTimeout(() => {
      if (!startedRef.current) {
        startedRef.current = true;
        setTimeout(() => setPhase('typing'), 0);
        setTimeout(() => setPhase('message'), 2500);
      }
    }, 8000);

    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      clearTimeout(fallback);
    };
  }, [onFirstInteraction]);

  const showBubble = !dismissed && (phase === 'typing' || phase === 'message');
  const showBadge = !dismissed && phase === 'message';

  return (
    <>
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
        {showBubble && (
          <div
            className="relative bg-white rounded-xl shadow-xl px-4 py-3 max-w-[260px]"
            style={{ animation: 'fadeSlideUp 0.3s ease-out' }}
          >
            {phase === 'message' && (
              <button
                onClick={() => setDismissed(true)}
                className="absolute -top-2 -right-2 w-5 h-5 bg-stone-500 hover:bg-stone-700 text-white rounded-full flex items-center justify-center text-xs leading-none transition-colors"
                aria-label="Dismiss"
              >
                x
              </button>
            )}

            {phase === 'typing' ? (
              <div className="flex items-center gap-1 py-1 px-1">
                <span className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            ) : (
              <p className="text-sm text-stone-800 font-medium leading-snug">
                Are you sourcing specific Italian products? Message us
              </p>
            )}

            <div className="absolute -bottom-1.5 right-5 w-3 h-3 bg-white rotate-45 shadow-sm" />
          </div>
        )}

        <a
          href="https://wa.me/393283915713?text=Hello,%20I'm%20interested%20in%20learning%20more%20about%20ALL-FOOD's%20Italian%20export%20services."
          target="_blank"
          rel="noopener noreferrer"
          className="group"
          aria-label="Chat on WhatsApp"
          data-testid="whatsapp-button"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-[#25D366] rounded-full animate-ping opacity-25" />
            <div className="relative w-14 h-14 bg-[#25D366] hover:bg-[#128C7E] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110">
              <svg viewBox="0 0 32 32" className="w-8 h-8 text-white fill-current" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.128 6.744 3.046 9.378L1.054 31.29l6.118-1.958C9.726 30.972 12.734 32 16.004 32 24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.336 22.594c-.39 1.1-1.932 2.014-3.168 2.28-.846.18-1.952.324-5.674-1.22-4.762-1.974-7.828-6.82-8.066-7.136-.228-.316-1.916-2.55-1.916-4.864 0-2.314 1.212-3.452 1.644-3.924.39-.428 1.022-.624 1.624-.624.196 0 .372.01.53.018.472.02.708.048 1.02.788.39.926 1.34 3.268 1.458 3.506.118.238.236.558.078.876-.148.326-.276.472-.514.748-.238.276-.464.488-.702.784-.218.258-.464.534-.2.984.266.452 1.182 1.946 2.536 3.152 1.742 1.55 3.206 2.032 3.66 2.26.352.178.77.148 1.046-.138.346-.364.774-.966 1.21-1.56.31-.422.702-.474 1.094-.316.398.148 2.52 1.188 2.952 1.404.432.218.72.326.826.502.108.178.108 1.028-.282 2.128z"/>
              </svg>
            </div>
            {showBadge && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-sm">
                1
              </span>
            )}
          </div>
        </a>
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default WhatsAppButton;
